import React, { useState } from 'react';
import { Box, Stack, Paper, Typography, Button } from '@mui/material';
import { Check } from 'lucide-react';
import { UserAssessment } from '../../../../../../utils/types/assessment_types';
import { BrowserSettingsCard } from './BrowserSettingsCard';
import { CameraSetupCard } from './CameraSetupCard';
import { FinalSubmitCard } from './FinalSubmitCard';
import { ModernStepper } from './ModernStepper';
import { StartRecordingCard } from './StartRecordingCard';
import { StopRecordingCard } from './StopRecordingCard';
import { SystemSetupCard } from './SystemSetupCard';

interface AssessmentInstructionsModalProps {
  setModalContent: (content: number) => void;
  setIsPractice: (content: boolean) => void;
  userAssessmentList: UserAssessment[];
  afterHelpModalContent?: number; // where to go after instructions complete
}

export const AssessmentInstructionsModal: React.FC<AssessmentInstructionsModalProps> = ({
  setModalContent,
  setIsPractice,
  userAssessmentList,
  afterHelpModalContent,
}) => {
  const [showCompletion, setShowCompletion] = useState(false);
  const HELP_SEEN_KEY = 'hiacuity_help_seen';

  const handleStepperComplete = () => {
    localStorage.setItem(HELP_SEEN_KEY, 'true');
    setShowCompletion(true);
  };

  const handleContinue = () => {
    const target = afterHelpModalContent ?? 2;
    setModalContent(target);
  };

  const instructionSteps = [
    {
      id: 1,
      title: 'Allow Web Browser Access to Camera and Microphone',
      description:
        '<b>Step 1:</b> Please allow the Web Browser access to your camera and microphone. See example below for Google Chrome - Click the <b>Allow</b> button:',
      component: <BrowserSettingsCard />,
    },
    {
      id: 2,
      title: 'System Setup: Camera & Microphone',
      description:
        '<b>Step 2:</b> Configure your camera and microphone for the assessment. Select your preferred devices below:',
      component: <CameraSetupCard />,
    },
    {
      id: 3,
      title: 'System Setup: Assessment Overview',
      description:
        '<b>Step 3:</b> Review your assessment details below — including total points and time allowed — then click <b>Next</b> when you are ready to begin:',
      component: <SystemSetupCard />,
    },
    {
      id: 4,
      title: 'Press Start Recording and speak your answer',
      description:
        '<b>Step 4:</b> Read the question, then press the <b>Start Recording</b> button and speak your answer:',
      component: <StartRecordingCard />,
    },
    {
      id: 5,
      title: 'Press Stop Recording when you have finished',
      description:
        '<b>Step 5:</b> When you have finished speaking your answer, press the <b>Stop Recording</b> button. You can then review and redo before submitting:',
      component: <StopRecordingCard />,
    },
    {
      id: 6,
      title: 'Press Submit',
      description:
        '<b>Step 6:</b> Press <b>Submit</b> to move on. Please note, you can Redo the recording by pressing the Redo Recording button:',
      component: <FinalSubmitCard />,
    },
  ];

  if (showCompletion) {
    return (
      <Paper
        variant='outlined'
        sx={{
          bgcolor: 'action.hover',
          p: 4,
          borderRadius: 3,
          textAlign: 'center',
        }}
      >
        <Stack spacing={2.5} alignItems='center'>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              bgcolor: 'success.main',
              color: 'common.white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: (theme) => `0 4px 14px ${theme.palette.success.light}`,
            }}
          >
            <Check size={32} strokeWidth={3} />
          </Box>
          <Box>
            <Typography variant='h5' fontWeight={700} color='text.primary'>
              Instructions Complete!
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
              You&apos;re ready to start your assessment. Choose an option below:
            </Typography>
          </Box>
          <Button
            variant='contained'
            color='primary'
            size='large'
            onClick={handleContinue}
            sx={{ px: 4 }}
          >
            Continue
          </Button>
        </Stack>
      </Paper>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.paper', p: 2 }}>
      <ModernStepper
        steps={instructionSteps}
        onComplete={handleStepperComplete}
      />
    </Box>
  );
};
